import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText, Download, Send, Eye } from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet, apiPost } from "@/lib/api";
import pt from "@/lib/i18n";

const STATUS_VARIANT = { draft: "neutral", sent: "info", viewed: "warning", accepted: "success", rejected: "error" };

const ProposalsPage = () => {
    const [proposals, setProposals] = useState(null);

    const load = async () => {
        try { setProposals(await apiGet("/proposals") || []); }
        catch (e) { setProposals([]); }
    };

    useEffect(() => { load(); }, []);

    const markSent = async (id) => {
        try {
            await apiPost(`/proposals/${id}/send`);
            await load();
        } catch (e) {}
    };

    return (
        <div data-testid="proposals-page" className="space-y-6">
            <PageHeader title={pt.proposals.title} subtitle={pt.proposals.subtitle} testId="proposals-header" />
            <GlassCard padding="p-0" className="overflow-hidden">
                {proposals === null ? (
                    <div className="p-4 space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-14" />)}</div>
                ) : proposals.length === 0 ? (
                    <p className="p-8 text-center font-body text-sm text-[color:var(--slate-mid)]">{pt.proposals.empty}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="proposals-table">
                            <thead className="bg-[color:var(--bg-surface)] text-[color:var(--slate-mid)] uppercase text-[10px] tracking-wider">
                                <tr>
                                    <th className="px-4 py-2.5 text-left">{pt.proposals.col_number}</th>
                                    <th className="px-4 py-2.5 text-left">{pt.proposals.col_customer}</th>
                                    <th className="px-4 py-2.5 text-right">{pt.proposals.col_total}</th>
                                    <th className="px-4 py-2.5 text-left">{pt.proposals.col_status}</th>
                                    <th className="px-4 py-2.5 text-left">{pt.proposals.col_created}</th>
                                    <th className="px-4 py-2.5 text-right">{pt.common.actions}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {proposals.map((p) => (
                                    <motion.tr
                                        key={p.id}
                                        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                        className="border-t border-[color:var(--glass-border)] hover:bg-[color:var(--copper-subtle)]/40 transition-colors"
                                        data-testid={`proposal-row-${p.id}`}
                                    >
                                        <td className="px-4 py-2.5 font-mono text-[11px] text-[color:var(--copper-dark)]">{p.proposal_number}</td>
                                        <td className="px-4 py-2.5 font-body text-[color:var(--slate-deep)]">
                                            {p.customer_company}
                                            <span className="block font-mono text-[10px] text-[color:var(--slate-mid)]">{p.contact_name}</span>
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                            <span className="font-mono text-[color:var(--slate-deep)] font-medium">USD {Number(p.total_usd).toLocaleString("pt-PT")}</span>
                                            <span className="block font-mono text-[10px] text-[color:var(--slate-mid)]">AOA {Number(p.total_aoa).toLocaleString("pt-PT")}</span>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <NxBadge variant={STATUS_VARIANT[p.status]}>{pt.proposals.statuses[p.status] || p.status}</NxBadge>
                                        </td>
                                        <td className="px-4 py-2.5 text-[color:var(--slate-mid)] font-mono text-xs">{new Date(p.created_at).toLocaleDateString("pt-PT")}</td>
                                        <td className="px-4 py-2.5 text-right">
                                            <div className="flex items-center gap-2 justify-end">
                                                {p.pdf_url && (
                                                    <a
                                                        href={`${process.env.REACT_APP_BACKEND_URL}${p.pdf_url}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[color:var(--copper-subtle)] text-[color:var(--copper-dark)] text-xs hover:bg-[color:var(--copper)] hover:text-white transition-colors"
                                                        data-testid={`btn-pdf-${p.id}`}
                                                    >
                                                        <Download size={11} /> {pt.proposals.downloadPdf}
                                                    </a>
                                                )}
                                                {p.status === "draft" && (
                                                    <NxButton variant="copper" size="sm" onClick={() => markSent(p.id)} icon={<Send size={11} />} data-testid={`btn-send-${p.id}`}>
                                                        Enviar
                                                    </NxButton>
                                                )}
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </GlassCard>
        </div>
    );
};

export default ProposalsPage;
